import React from 'react';
import { FileText, Package, Bell, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

interface Activity {
  id: string;
  type: 'blog' | 'product' | 'other';
  title: string;
  date: string;
}

interface ActivityFeedProps {
  activities: Activity[];
  title?: string;
  showViewAll?: boolean;
  viewAllLink?: string;
  emptyMessage?: string;
}

const ActivityFeed: React.FC<ActivityFeedProps> = ({ 
  activities, 
  title = "Activité Récente", 
  showViewAll = false,
  viewAllLink = "/admin/activity",
  emptyMessage = "Aucune activité récente"
}) => {
  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'blog':
        return <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600"><FileText size={16} /></div>;
      case 'product':
        return <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600"><Package size={16} /></div>;
      default:
        return <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-600"><Bell size={16} /></div>;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return "Aujourd'hui";
    } else if (diffDays === 1) {
      return "Hier";
    } else if (diffDays < 7) {
      return `Il y a ${diffDays} jours`;
    } else if (diffDays < 30) {
      return `Il y a ${Math.floor(diffDays / 7)} semaines`;
    } else {
      return `Il y a ${Math.floor(diffDays / 30)} mois`;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        {showViewAll && activities.length > 0 && (
          <Link to={viewAllLink} className="text-sm text-primary-600 hover:text-primary-700 flex items-center">
            Voir tout
            <ExternalLink size={14} className="ml-1" />
          </Link>
        )}
      </div>
      
      {activities.length > 0 ? (
        <div className="space-y-4">
          {activities.map((activity, index) => (
            <div key={index} className="flex items-start gap-3 pb-4 border-b border-gray-100 last:border-0 last:pb-0">
              {getActivityIcon(activity.type)}
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {activity.type === 'blog' ? 'Nouvel article' : 
                   activity.type === 'product' ? 'Nouveau produit' : 
                   'Nouvelle activité'}: {activity.title}
                </p>
                <p className="text-xs text-gray-500">{formatDate(activity.date)}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-6 text-gray-400">
          <Bell size={32} className="mb-2" />
          <p className="text-sm">{emptyMessage}</p>
        </div>
      )}
    </div>
  );
};

export default ActivityFeed;